import React from 'react';
import styled from 'styled-components';
import { ShoppingCart, Plus, Minus, Trash2, ArrowLeft, CreditCard, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';

const PageContainer = styled.div`
  padding-top: calc(var(--header-h) + 40px);
  padding-bottom: 80px;
  min-height: 100vh;
  background: white;
  width: 100%;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #333;
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 30px;
  margin-left: 0;
  transition: color 0.3s ease;

  &:hover {
    color: #666;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 50px;

  h1 {
    font-size: 2.5rem;
    color: #333;
    margin-bottom: 15px;
    font-weight: 300;
  }

  p {
    font-size: 1.1rem;
    color: #666;
    margin: 0;
  }
`;

const CartContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const CartItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CartItem = styled.div`
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e0e0e0;
  display: grid;
  grid-template-columns: 120px 1fr auto;
  gap: 20px;
  align-items: center;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const ItemImage = styled.div<{ $bgImage: string }>`
  width: 120px;
  height: 120px;
  background: url(${props => props.$bgImage}) center/cover;
  border-radius: 12px;
`;

const ItemDetails = styled.div`
  h3 {
    font-size: 1.2rem;
    color: #333;
    margin-bottom: 8px;
    font-weight: 600;
  }

  p {
    color: #666;
    font-size: 0.9rem;
    margin-bottom: 10px;
  }

  .size {
    color: #333;
    font-weight: 600;
    font-size: 0.9rem;
  }
`;

const ItemActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const QuantityButton = styled.button`
  width: 35px;
  height: 35px;
  border: 2px solid #333;
  background: white;
  color: #333;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: #333;
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Quantity = styled.span`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  min-width: 30px;
  text-align: center;
`;

const Price = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: #333;
  text-align: center;
`;

const RemoveButton = styled.button`
  background: #ff4757;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.9rem;
  transition: background 0.3s ease;

  &:hover {
    background: #ff3742;
  }
`;

const CartSummary = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e0e0e0;
  height: fit-content;
  position: sticky;
  top: 140px;
`;

const SummaryTitle = styled.h3`
  font-size: 1.3rem;
  color: #333;
  margin-bottom: 20px;
  font-weight: 600;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
    font-weight: 700;
    font-size: 1.1rem;
    color: #333;
  }
`;

const InfoBox = styled.div`
  margin-top: 20px;
  padding: 15px;
  background: #f8f4f0;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #666;
`;

const CheckoutButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #333 0%, #555 100%);
  color: white;
  border: none;
  padding: 15px 20px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(26, 26, 26, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(26, 26, 26, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e0e0e0;

  h2 {
    font-size: 2rem;
    color: #333;
    margin-bottom: 20px;
    font-weight: 300;
  }

  p {
    font-size: 1.1rem;
    color: #666;
    margin-bottom: 30px;
  }

  button {
    background: #333;
    color: white;
    border: none;
    padding: 15px 30px;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.3s ease;

    &:hover {
      background: #555;
    }
  }
`;

const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const { cartItems, updateQuantity, removeFromCart, getTotalPrice } = useCart();

  const subtotal = getTotalPrice();
  const shipping = subtotal > 10000 ? 0 : 500;
  const total = subtotal + shipping;

  if (cartItems.length === 0) {
    return (
      <PageContainer>
        <Container>
          <BackButton onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
            Geri Dön
          </BackButton>

          <EmptyCart>
            <ShoppingCart size={60} style={{ color: '#333', marginBottom: '20px' }} />
            <h2>Sepetiniz Boş</h2>
            <p>Henüz sepetinize ürün eklemediniz. Ürünlerimizi keşfedin!</p>
            <button onClick={() => navigate('/gelinlikler')}>
              Ürünleri Keşfet
            </button>
          </EmptyCart>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <BackButton onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          Geri Dön
        </BackButton>

        <Header>
          <h1>Sepetim</h1>
          <p>Seçtiğiniz ürünleri gözden geçirin ve siparişinizi tamamlayın</p>
        </Header>

        <CartContainer>
          <CartItems>
            {cartItems.map(item => (
              <CartItem key={item.id}>
                <ItemImage $bgImage={item.image} />
                
                <ItemDetails>
                  <h3>{item.name}</h3>
                  <p>{item.description}</p>
                  <div className="size">Beden: {item.size}</div>
                </ItemDetails>

                <ItemActions>
                  <QuantityControls>
                    <QuantityButton
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      <Minus size={16} />
                    </QuantityButton>
                    <Quantity>{item.quantity}</Quantity>
                    <QuantityButton
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    >
                      <Plus size={16} />
                    </QuantityButton>
                  </QuantityControls>

                  <Price>₺{(item.price * item.quantity).toLocaleString()}</Price>

                  <RemoveButton onClick={() => removeFromCart(item.id)}>
                    <Trash2 size={16} />
                    Kaldır
                  </RemoveButton>
                </ItemActions>
              </CartItem>
            ))}
          </CartItems>

          <CartSummary>
            <SummaryTitle>Sipariş Özeti</SummaryTitle>
            
            <SummaryRow>
              <span>Ara Toplam</span>
              <span>₺{subtotal.toLocaleString()}</span>
            </SummaryRow>
            
            <SummaryRow>
              <span>Kargo</span>
              <span>{shipping === 0 ? 'Ücretsiz' : `₺${shipping.toLocaleString()}`}</span>
            </SummaryRow>
            
            <SummaryRow>
              <span>Toplam</span>
              <span>₺{total.toLocaleString()}</span>
            </SummaryRow>

            <CheckoutButton onClick={() => navigate('/odeme')}>
              <CreditCard size={20} />
              Ödeme
            </CheckoutButton>

            <InfoBox>
              <Truck size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              ₺10.000 üzeri siparişlerde ücretsiz kargo!
            </InfoBox>
          </CartSummary>
        </CartContainer>
      </Container>
    </PageContainer>
  );
};

export default CartPage;
